import React, { useState, useEffect, useRef } from 'react';
import { X, Dumbbell, DollarSign, Users, CalendarDays, Clock, AlertCircle, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { sportTypeAPI, academyAPI } from '../../services/api';
import { uploadFile, uploadErrorMessage } from '../../services/imageUploadService';
import TermsBuilderModal from '../../tournaments/components/TermsBuilderModal';
import DocumentViewerModal from '../../tournaments/components/DocumentViewerModal';
import toast from 'react-hot-toast';

/**
 * CreateProgramModal - Modal for creating/editing a Program
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - onSave: function(programObject)
 * - editingProgram: object | null
 */

// Mirrors OFFERING_PRESETS in the API (src/academies/offeringPresets.js). Only
// the copy lives here — the behaviour is resolved and stored server-side, so
// this list decides what the form says, never what the offering does.
const OFFERING_TYPES = [
  {
    id: 'course',
    label: 'Program',
    blurb: 'A course over a term. Players enrol once and attend every session.',
    feeLabel: 'Monthly Fee',
    feeHint: 'Amount in USD',
    feePlaceholder: 'e.g., 150',
    capacityLabel: 'Max Participants',
    capacityHint: 'Leave blank for unlimited',
    namePlaceholder: 'e.g., Junior Development Program',
  },
  {
    id: 'open_play',
    label: 'Open Play',
    blurb: 'Drop-in sessions. Players book and pay for a single night.',
    feeLabel: 'Price per Player',
    feeHint: 'Charged per player, per session',
    feePlaceholder: 'e.g., 15',
    capacityLabel: 'Spots per Session',
    capacityHint: 'Extra players go on the waitlist',
    namePlaceholder: 'e.g., Wednesday Open Play — Beginner',
  },
  {
    id: 'meetup',
    label: 'Meetup',
    blurb: 'A community that plays together. Free by default, with a group chat.',
    feeLabel: 'Price per Player',
    feeHint: 'Leave blank if the meetup is free',
    feePlaceholder: 'e.g., 0',
    capacityLabel: 'Spots per Session',
    capacityHint: 'Extra players go on the waitlist',
    namePlaceholder: 'e.g., Sunday Social Pickleball',
  },
];
const typeConfig = (id) => OFFERING_TYPES.find(t => t.id === id) || OFFERING_TYPES[0];

const EMPTY_FORM = {
  programType: 'course',
  name: '',
  description: '',
  sportTypeId: '',
  level: 'beginner',
  ageGroup: '',
  fee: '',
  maxParticipants: '',
  image: '',
  registrationOpen: true,
  requireTerms: false,
  requireWaiver: false,
  registrationFields: [],
  startDate: '',
  endDate: '',
  timeFrom: '',
  timeTo: '',
  registrationDueDate: '',
  lateFee: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

const CreateProgramModal = ({ isOpen, onClose, onSave, editingProgram = null, academyId }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isEditMode = !!editingProgram;

  const [formData, setFormData] = useState(EMPTY_FORM);
  // Drives the copy on the form — labels, hints and placeholders.
  const cfg = typeConfig(formData.programType);
  const [sportTypes, setSportTypes] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Which document ('terms' | 'waiver') the clause builder / viewer is open for.
  const [buildingDoc, setBuildingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  // Paths live in local state so a freshly generated doc shows immediately,
  // before the parent list has refetched.
  const [docPaths, setDocPaths] = useState({ terms: null, waiver: null });
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const docFileRef = useRef(null);
  const imageInputRef = useRef(null);

  // Fetch sport types on mount
  useEffect(() => {
    const loadSportTypes = async () => {
      try {
        const response = await sportTypeAPI.getAll();
        setSportTypes(response?.data || response || []);
      } catch (error) {
        console.error('Failed to load sport types:', error);
      }
    };
    loadSportTypes();
  }, []);

  // Pre-fill when editing or reset when modal opens/closes
  useEffect(() => {
    if (isOpen && editingProgram) {
      setFormData({
        name: editingProgram.name || '',
        description: editingProgram.description || '',
        sportTypeId: editingProgram.sportTypeId ? String(editingProgram.sportTypeId) : '',
        level: editingProgram.level || 'beginner',
        ageGroup: editingProgram.ageGroup || '',
        fee: editingProgram.fee != null ? String(editingProgram.fee) : '',
        programType: editingProgram.programType || 'course',
        maxParticipants: editingProgram.maxParticipants != null ? String(editingProgram.maxParticipants) : '',
        image: editingProgram.imageUrl || '',
        registrationOpen: editingProgram.registrationOpen !== undefined ? editingProgram.registrationOpen : true,
        requireTerms: !!editingProgram.requireTerms,
        requireWaiver: !!editingProgram.requireWaiver,
        registrationFields: Array.isArray(editingProgram.registrationFields) ? editingProgram.registrationFields : [],
        startDate: editingProgram.startDate || '',
        endDate: editingProgram.endDate || '',
        timeFrom: editingProgram.timeFrom || '',
        timeTo: editingProgram.timeTo || '',
        registrationDueDate: editingProgram.registrationDueDate || '',
        lateFee: editingProgram.lateFee != null ? String(editingProgram.lateFee) : '',
        contactName: editingProgram.contactName || '',
        contactEmail: editingProgram.contactEmail || '',
        contactPhone: editingProgram.contactPhone || '',
      });
    } else if (!isOpen) {
      setFormData(EMPTY_FORM);
    }
  }, [isOpen, editingProgram]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Please upload a valid image file.'); return; }
    setUploadingImage(true);
    try {
      const { url } = await uploadFile(file, 'group_assets');
      setFormData((prev) => ({ ...prev, image: url }));
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setUploadingImage(false);
    }
  };

  // PDF alternative to the clause builder. /upload/init requires entityPath to
  // start with the caller's own user id (an anti-IDOR guard), so the program
  // scoping goes after it rather than at the front.
  const handleDocUpload = async (kind, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files are allowed'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25 MB'); return; }

    setUploadingDoc(kind);
    try {
      const entityPath = `${user?.id}/programs/${editingProgram.id}/${kind}-${Date.now()}.pdf`;
      const { key } = await uploadFile(file, 'academy_documents', file.name, entityPath);
      await academyAPI.programs.update(academyId, editingProgram.id, {
        [kind === 'terms' ? 'termsContentPath' : 'waiverContentPath']: key,
      });
      setDocPaths(prev => ({ ...prev, [kind]: key }));
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a program name');
      return;
    }

    if (!formData.sportTypeId) {
      toast.error('Please select a sport');
      return;
    }

    const selectedSport = sportTypes.find((s) => s.id === parseInt(formData.sportTypeId));

    const program = {
      id: editingProgram ? editingProgram.id : Date.now(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      sportName: selectedSport?.sportName || null,
      sportTypeId: parseInt(formData.sportTypeId) || null,
      level: formData.level,
      ageGroup: formData.ageGroup.trim(),
      fee: formData.fee ? parseFloat(formData.fee) : null,
      maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
      imageUrl: formData.image.trim() || null,
      registrationOpen: formData.registrationOpen,
      requireTerms: formData.requireTerms,
      requireWaiver: formData.requireWaiver,
      registrationFields: formData.registrationFields,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      timeFrom: formData.timeFrom || null,
      timeTo: formData.timeTo || null,
      registrationDueDate: formData.registrationDueDate || null,
      lateFee: formData.lateFee ? parseFloat(formData.lateFee) : null,
      contactName: formData.contactName.trim() || null,
      contactEmail: formData.contactEmail.trim() || null,
      contactPhone: formData.contactPhone.trim() || null,
      classes: editingProgram ? editingProgram.classes || [] : [],
      // Only on create. The type decides registration shape (term vs per-night),
      // which the API locks once anyone has registered, so it is not editable here.
      ...(isEditMode ? {} : { programType: formData.programType }),
    };

    // No toast here — onSave owns the outcome. It's the one that knows whether
    // the request actually succeeded; announcing it from this side fired a
    // second "created successfully!" alongside the real one, and claimed
    // success even when the save had failed.
    if (onSave) {
      onSave(program);
    }

    handleClose();
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto`}>
        {/* Sticky header */}
        <div className={`sticky top-0 bg-inherit border-b ${theme.border} px-6 py-4 flex items-center justify-between z-10`}>
          <div>
            <h3 className={`text-xl font-semibold ${theme.text}`}>
              {isEditMode ? 'Edit Program' : 'Create New Program'}
            </h3>
            <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
              {isEditMode ? 'Update program information' : 'Set up a new training program for your academy'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
            aria-label="Close"
          >
            <X size={18} className={theme.textMuted} />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* What kind of offering. Create-only: the type decides whether a
              registration buys the term or a single night, and the API rejects
              changing that once anyone has registered. */}
          {!isEditMode && (
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>Type</label>
              <div className="grid grid-cols-3 gap-2">
                {OFFERING_TYPES.map(t => {
                  const active = formData.programType === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setFormData(f => ({ ...f, programType: t.id }))}
                      aria-pressed={active}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        active ? `${theme.tabActive} border-transparent` : `${theme.border} ${theme.text} ${theme.cardHover}`}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <p className={`text-xs ${theme.textMuted} mt-1.5`}>{cfg.blurb}</p>
            </div>
          )}

          {/* Program Name */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              {cfg.label} Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={cfg.namePlaceholder}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe what this program offers..."
              rows={3}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
            />
          </div>

          {/* Sport */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              <Dumbbell size={15} className="inline mr-1" />
              Sport <span className="text-red-500">*</span>
            </label>
            <select
              name="sportTypeId"
              value={formData.sportTypeId}
              onChange={handleChange}
              required
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="">Select a sport...</option>
              {sportTypes.map((sport) => (
                <option key={sport.id} value={sport.id}>
                  {sport.sportName}
                </option>
              ))}
            </select>
          </div>

          {/* Level */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>Level</label>
            <select
              name="level"
              value={formData.level}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Age Group */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              <Users size={15} className="inline mr-1" />
              Age Group
            </label>
            <input
              type="text"
              name="ageGroup"
              value={formData.ageGroup}
              onChange={handleChange}
              placeholder="e.g., Ages 8–14"
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>

          {/* Fee + Max Participants */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                <DollarSign size={15} className="inline mr-1" />
                {cfg.feeLabel}
              </label>
              <input
                type="number"
                name="fee"
                value={formData.fee}
                onChange={handleChange}
                placeholder={cfg.feePlaceholder}
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              <p className={`text-xs ${theme.textMuted} mt-1`}>{cfg.feeHint}</p>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                <Users size={15} className="inline mr-1" />
                {cfg.capacityLabel}
              </label>
              <input
                type="number"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleChange}
                placeholder="e.g., 20"
                min="1"
                step="1"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              <p className={`text-xs ${theme.textMuted} mt-1`}>{cfg.capacityHint}</p>
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              Cover Image <span className={`text-xs font-normal ${theme.textMuted}`}>(optional)</span>
            </label>
            {formData.image ? (
              <div className="relative mb-2">
                <img
                  src={formData.image}
                  alt="Preview"
                  className="h-32 w-full object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, image: '' }))}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-full transition"
                  title="Remove cover image"
                >
                  <X size={13} className="text-white" />
                </button>
              </div>
            ) : (
              <div className={`h-32 w-full mb-2 rounded-lg border-2 border-dashed ${theme.border} flex items-center justify-center`}>
                <div className={`text-center ${theme.textMuted}`}>
                  <ImageIcon size={22} className="mx-auto mb-1" />
                  <p className="text-xs">No cover image set</p>
                </div>
              </div>
            )}
            <label className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer text-sm font-medium transition ${
              uploadingImage ? 'opacity-50 pointer-events-none' : `${theme.border} ${theme.text} hover:bg-gray-50 dark:hover:bg-gray-800`
            }`}>
              {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploadingImage ? 'Uploading…' : formData.image ? 'Replace image' : 'Upload from device'}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { handleImageUpload(e.target.files[0]); e.target.value = ''; }}
                disabled={uploadingImage}
              />
            </label>
          </div>

          {/* Program dates */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              <CalendarDays size={15} className="inline mr-1" />
              Program Duration
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs ${theme.textMuted} mb-1`}>Start Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
                />
              </div>
              <div>
                <label className={`block text-xs ${theme.textMuted} mb-1`}>End Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate || undefined}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
                />
              </div>
            </div>
          </div>

          {/* Session time window (optional) */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              <Clock size={15} className="inline mr-1" />
              Typical Session Time{' '}
              <span className={`text-xs font-normal ${theme.textMuted}`}>(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs ${theme.textMuted} mb-1`}>From</label>
                <input
                  type="time"
                  name="timeFrom"
                  value={formData.timeFrom}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
                />
              </div>
              <div>
                <label className={`block text-xs ${theme.textMuted} mb-1`}>To</label>
                <input
                  type="time"
                  name="timeTo"
                  value={formData.timeTo}
                  onChange={handleChange}
                  min={formData.timeFrom || undefined}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
                />
              </div>
            </div>
            <p className={`text-xs ${theme.textMuted} mt-1`}>Used as the default time range when creating squad sessions</p>
          </div>

          {/* Registration due date + late fee */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                <CalendarDays size={14} className="inline mr-1" />
                Registration Due Date
                <span className={`ml-1 text-xs font-normal ${theme.textMuted}`}>(optional)</span>
              </label>
              <input
                type="date"
                name="registrationDueDate"
                value={formData.registrationDueDate}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                <AlertCircle size={14} className="inline mr-1" />
                Late Fee
                <span className={`ml-1 text-xs font-normal ${theme.textMuted}`}>(optional)</span>
              </label>
              <input
                type="number"
                name="lateFee"
                value={formData.lateFee}
                onChange={handleChange}
                placeholder="e.g. 25"
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
              />
              <p className={`text-xs ${theme.textMuted} mt-1`}>Charged after due date</p>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
              Contact Information
              <span className={`ml-1 text-xs font-normal ${theme.textMuted}`}>(shown on flyers)</span>
            </label>
            <div className="space-y-2">
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                placeholder="Contact name"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
              />
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder="Contact email"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
              />
              <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleChange}
                placeholder="Contact phone"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
              />
            </div>
          </div>

          {/* Registration Open toggle */}
          <div className={`flex items-center justify-between p-3 rounded-lg border ${theme.border}`}>
            <div>
              <p className={`text-sm font-medium ${theme.text}`}>Registration Open</p>
              <p className={`text-xs ${theme.textMuted}`}>Allow new enrollments immediately</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="registrationOpen"
                checked={formData.registrationOpen}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-checked:bg-blue-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
            </label>
          </div>

          {/* Registration requirements */}
          <div className={`p-3 rounded-lg border ${theme.border} space-y-3`}>
            <p className={`text-sm font-semibold ${theme.text}`}>Registration requirements</p>

            {[
              { kind: 'terms',  label: 'Terms & Conditions' },
              { kind: 'waiver', label: 'Liability Waiver' },
            ].map(({ kind, label }) => {
              const key = kind === 'terms' ? 'requireTerms' : 'requireWaiver';
              // Locally generated path wins — it's newer than the loaded program.
              const savedPath = kind === 'terms' ? editingProgram?.termsContentPath : editingProgram?.waiverContentPath;
              const docPath = docPaths[kind] || savedPath;
              return (
                <div key={kind}>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox" name={key} checked={formData[key]} onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm ${theme.text}`}>Require {label}</span>
                  </label>

                  {formData[key] && (
                    // Documents attach to a saved program (the S3 key includes its
                    // id), so authoring only becomes available after creation.
                    isEditMode ? (
                      <div className="ml-6 mt-1.5 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setBuildingDoc(kind)}
                          className="px-2.5 py-1 border border-purple-300 text-purple-600 rounded-lg text-xs font-medium hover:bg-purple-50 transition"
                        >
                          {docPath ? 'Replace document' : 'Create from template'}
                        </button>
                        <label className={`px-2.5 py-1 border ${theme.border} ${theme.text} rounded-lg text-xs font-medium ${theme.cardHover} transition cursor-pointer ${
                          uploadingDoc === kind ? 'opacity-50 pointer-events-none' : ''
                        }`}>
                          {uploadingDoc === kind ? 'Uploading…' : 'Upload PDF'}
                          <input
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => { handleDocUpload(kind, e.target.files?.[0]); e.target.value = ''; }}
                            disabled={uploadingDoc === kind}
                          />
                        </label>
                        {docPath && (
                          <button
                            type="button"
                            onClick={() => setViewingDoc(kind)}
                            className={`px-2.5 py-1 border ${theme.border} ${theme.text} rounded-lg text-xs font-medium ${theme.cardHover} transition`}
                          >
                            View
                          </button>
                        )}
                        {!docPath && (
                          <span className="text-xs text-amber-600">
                            No document yet — registrants will be asked to accept with nothing to read.
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className={`text-xs ${theme.textMuted} ml-6 mt-1`}>
                        You can write the document once the program is created.
                      </p>
                    )
                  )}
                </div>
              );
            })}
          </div>

          {/* Additional information questions */}
          <div className={`p-3 rounded-lg border ${theme.border} space-y-3`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-semibold ${theme.text}`}>Additional information</p>
                <p className={`text-xs ${theme.textMuted}`}>Questions asked during registration</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  registrationFields: [...prev.registrationFields, {
                    // Stable, collision-free key — responses are stored against it.
                    id: `field_${Date.now()}`, label: '', type: 'text', required: false,
                  }],
                }))}
                className={`px-2.5 py-1 border ${theme.border} ${theme.text} rounded-lg text-xs font-medium ${theme.cardHover} transition`}
              >
                + Add question
              </button>
            </div>

            {formData.registrationFields.length === 0 && (
              <p className={`text-xs ${theme.textMuted}`}>No extra questions — registrants go straight to review.</p>
            )}

            {formData.registrationFields.map((f, i) => {
              const patch = (changes) => setFormData(prev => ({
                ...prev,
                registrationFields: prev.registrationFields.map((x, xi) => xi === i ? { ...x, ...changes } : x),
              }));
              return (
                <div key={f.id} className={`p-2.5 rounded-lg border ${theme.border} space-y-2`}>
                  <div className="flex gap-2">
                    <input
                      value={f.label}
                      onChange={e => patch({ label: e.target.value })}
                      placeholder="Question label, e.g. Emergency contact"
                      className={`flex-1 px-2.5 py-1.5 border ${theme.border} rounded-lg ${theme.input} text-xs`}
                    />
                    <select
                      value={f.type}
                      onChange={e => patch({ type: e.target.value, ...(e.target.value !== 'select' && { options: undefined }) })}
                      className={`px-2 py-1.5 border ${theme.border} rounded-lg ${theme.input} text-xs`}
                    >
                      {['text', 'textarea', 'select', 'checkbox', 'date', 'phone', 'email'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        registrationFields: prev.registrationFields.filter((_, xi) => xi !== i),
                      }))}
                      className="px-2 py-1.5 border border-red-200 text-red-500 rounded-lg text-xs hover:bg-red-50 transition"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  {f.type === 'select' && (
                    <input
                      value={(f.options || []).join(', ')}
                      onChange={e => patch({ options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })}
                      placeholder="Choices, comma separated — e.g. S, M, L"
                      className={`w-full px-2.5 py-1.5 border ${theme.border} rounded-lg ${theme.input} text-xs`}
                    />
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!f.required} onChange={e => patch({ required: e.target.checked })} className="w-3.5 h-3.5" />
                    <span className={`text-xs ${theme.textSecondary}`}>Required</span>
                  </label>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className={`flex gap-3 pt-2 border-t ${theme.border}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} transition-colors text-sm`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg transition-colors text-sm font-medium`}
            >
              {isEditMode ? 'Update Program' : 'Create Program'}
            </button>
          </div>
        </form>
      </div>

      {buildingDoc && (
        <TermsBuilderModal
          type={buildingDoc}
          entityName={formData.name || editingProgram?.name}
          ownerLabel="Program"
          templatePath="/academies/programs/document-templates"
          generatePath={`/academies/${academyId}/programs/${editingProgram?.id}/documents/generate`}
          onSaved={(path) => setDocPaths(prev => ({ ...prev, [buildingDoc]: path }))}
          onClose={() => setBuildingDoc(null)}
        />
      )}

      {viewingDoc && (
        <DocumentViewerModal
          type={viewingDoc}
          docPath={`/academies/${academyId}/programs/${editingProgram?.id}/documents/${viewingDoc}`}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
};

export default CreateProgramModal;
